import React, { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useGuestFilterOptions } from "../hooks/useGuestFilterOptions";

type GuestFilterSelectFieldProps = {
  hotelId?: string;
  value?: string;
  onChange: (value?: string) => void;
  triggerClassName?: string;
};

export const GuestFilterSelectField: React.FC<GuestFilterSelectFieldProps> = ({
  hotelId,
  value,
  onChange,
  triggerClassName,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading } = useGuestFilterOptions({
    hotelId,
    name: debouncedSearchTerm,
    enabled: open,
  });

  const options = useMemo(() => data?.items || [], [data?.items]);

  const selectedGuest = useMemo(
    () => options.find((item) => item.id === value),
    [options, value],
  );

  const triggerLabel = !hotelId
    ? t("billing.selectHotelFirst")
    : selectedGuest?.displayName || t("billing.allGuests");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="span"
          role="combobox"
          aria-expanded={open}
          aria-label={t("billing.searchGuest")}
          disabled={!hotelId}
          className={cn(
            "flex h-12 min-w-[220px] items-center justify-between gap-2 rounded-md border border-base-border bg-white px-4 py-2 text-sm text-black/96 disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronDown className="size-4 shrink-0 text-black" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="border-b p-2">
          <div className="flex items-center rounded-md border px-2">
            <Search className="mr-2 size-4 shrink-0 opacity-50" />
            <Input
              placeholder={t("billing.searchGuest")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 border-0 px-0 focus-visible:ring-0"
            />
          </div>
        </div>

        <div className="max-h-[320px] overflow-y-auto p-1">
          <Button
            type="button"
            variant="span"
            aria-pressed={!value}
            aria-label={t("billing.allGuests")}
            className={cn(
              "flex !h-auto min-h-8 w-full items-center justify-start gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
              !value && "bg-accent",
            )}
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
          >
            <Checkbox
              checked={!value}
              tabIndex={-1}
              aria-hidden
              className="pointer-events-none h-4 w-4 rounded-sm border-primary data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
            />
            <span>{t("billing.allGuests")}</span>
          </Button>

          {isLoading ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              {t("billing.loadingGuests")}
            </div>
          ) : null}

          {!isLoading && options.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              {t("billing.noGuestsFound")}
            </div>
          ) : null}

          {!isLoading && options.length > 0 ? (
            <div className="space-y-1">
              {options.map((guest) => (
                <Button
                  key={guest.id}
                  type="button"
                  variant="span"
                  aria-pressed={value === guest.id}
                  aria-label={guest.displayName}
                  className={cn(
                    "flex !h-auto min-h-14 w-full items-start justify-start gap-2 whitespace-normal rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                    value === guest.id && "bg-accent",
                  )}
                  onClick={() => {
                    onChange(guest.id);
                    setOpen(false);
                  }}
                >
                  <Checkbox
                    checked={value === guest.id}
                    tabIndex={-1}
                    aria-hidden
                    className="pointer-events-none mt-0.5 h-4 w-4 rounded-sm border-primary data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">
                      {guest.displayName}
                    </span>
                    {guest.email ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {guest.email}
                      </span>
                    ) : null}
                    {guest.phone ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {guest.phone}
                      </span>
                    ) : null}
                  </div>
                </Button>
              ))}
            </div>
          ) : null}
        </div>

        {value ? (
          <div className="border-t bg-muted/50 p-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-full"
              onClick={() => onChange(undefined)}
            >
              {t("billing.clearGuest")}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
